/**
 * Footer for Flagged Item modal: optional Nexsyis refresh, Cancel, Match, and optional Done.
 */
import React from 'react';
import { Button, Flex, HStack } from '@chakra-ui/react';
import type { InvoicePart } from '../../types/invoice';

const ACCENT = '#0071E3';
const ACCENT_HOVER = '#3544a0';

export interface FlaggedItemFooterProps {
    onClose: () => void;
    onMatch: () => void;
    /** Called when user clicks Done and there is no selection (close + optional toast) */
    onDoneWithoutMatch?: () => void;
    showMatchButton: boolean;
    effectiveSelected: InvoicePart | null;
    hasChanges: () => boolean;
    /** When true, show Done button instead of Match (for flag with partIndex when no options) */
    showDoneButton?: boolean;
    /** Re-fetch NEX PO data and re-run reconciliation (same filtering as initial load). */
    onRefreshNexsyisData?: () => void;
    isRefreshNexsyisPending?: boolean;
}

export function FlaggedItemFooter({
    onClose,
    onMatch,
    onDoneWithoutMatch,
    showMatchButton,
    effectiveSelected,
    hasChanges,
    showDoneButton = false,
    onRefreshNexsyisData,
    isRefreshNexsyisPending = false,
}: FlaggedItemFooterProps) {
    const showRefresh = typeof onRefreshNexsyisData === 'function';

    return (
        <Flex w="100%" justify="space-between" align="center" flexWrap="wrap" gap={3}>
            {showRefresh ? (
                <Button
                    variant="outline"
                    colorScheme="blue"
                    isLoading={isRefreshNexsyisPending}
                    isDisabled={isRefreshNexsyisPending}
                    onClick={() => onRefreshNexsyisData?.()}
                >
                    Refresh Nexsyis data
                </Button>
            ) : (
                <span />
            )}
            <HStack spacing={3}>
                <Button variant="outlineGray" onClick={onClose}>Cancel</Button>
                {showMatchButton && (
                    <Button
                        bg={ACCENT}
                        color="white"
                        _hover={{ bg: ACCENT_HOVER }}
                        _disabled={{ bg: 'gray.300', color: 'gray.500', cursor: 'not-allowed' }}
                        isDisabled={!effectiveSelected || isRefreshNexsyisPending}
                        onClick={onMatch}
                    >
                        Match
                    </Button>
                )}
                {showDoneButton && !showMatchButton && (
                    <Button
                        bg={ACCENT}
                        color="white"
                        _hover={{ bg: ACCENT_HOVER }}
                        _disabled={{ bg: 'gray.300', color: 'gray.500', cursor: 'not-allowed' }}
                        isDisabled={(!hasChanges() && !effectiveSelected) || isRefreshNexsyisPending}
                        onClick={() => {
                            if (effectiveSelected) {
                                onMatch();
                            } else {
                                onDoneWithoutMatch?.() ?? onClose();
                            }
                        }}
                    >
                        Done
                    </Button>
                )}
            </HStack>
        </Flex>
    );
}
